import { useCallback } from 'react';
import * as React from 'react';
import { LinkProps } from 'react-router-dom';
import { Link } from 'react-router-dom-v5-compat';
import tw, { styled } from 'twin.macro';
import 'styled-components/macro';

import { useUniqueID } from 'support/react/hooks';

import { useAbstractDropdownContext } from './AbstractDropdownContext';
import { AbstractDropdownItemStatic } from './AbstractDropdownItemStatic';
import { ABSTRACT_DROPDOWN_ITEM_ID_PREFIX } from './constants';

export type AbstractDropdownProps =
  | AbstractDropdownItemAnchorProps
  | AbstractDropdownItemButtonProps
  | AbstractDropdownItemLinkProps;

export type AbstractDropdownItemAnchorProps = Omit<React.ComponentPropsWithoutRef<'a'>, OmittedProps> &
  AdditionalProps<HTMLAnchorElement> & { href: string };
export type AbstractDropdownItemLinkProps = Omit<LinkProps, OmittedProps> & AdditionalProps<HTMLAnchorElement>;
export type AbstractDropdownItemButtonProps = Omit<React.ComponentPropsWithoutRef<'button'>, OmittedProps> &
  AdditionalProps<HTMLButtonElement>;

type OmittedProps = 'id' | 'onMouseEnter' | 'prefix' | 'tabIndex' | 'type';

interface AdditionalProps<T> {
  consumerName?: string;

  /**
   * Reference to the underlying HTML element.
   */
  innerRef?: React.Ref<T>;

  providerName?: string;
  postfix?: React.ReactNode;
  prefix?: React.ReactNode;
}

export function AbstractDropdownItem(props: AbstractDropdownItemAnchorProps): JSX.Element;
export function AbstractDropdownItem(props: AbstractDropdownItemButtonProps): JSX.Element;
export function AbstractDropdownItem(props: AbstractDropdownItemLinkProps): JSX.Element;
export function AbstractDropdownItem(props: AbstractDropdownProps): JSX.Element;

export function AbstractDropdownItem({
  children,
  consumerName = 'AbstractDropdownItem',
  onClick,
  postfix,
  prefix,
  providerName,
  ...polymorphicProps
}: AbstractDropdownProps) {
  const dropdown = useAbstractDropdownContext(consumerName, providerName);
  const itemID = useUniqueID(ABSTRACT_DROPDOWN_ITEM_ID_PREFIX);

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      if (dropdown.shouldCollapseOnItemClick) {
        dropdown.setIsExpanded(false);
      }
      (onClick as React.MouseEventHandler)?.(event);
    },
    [dropdown, onClick],
  );

  const handleMouseEnter = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.currentTarget.focus();
  }, []);

  const content = (
    <>
      {prefix && (
        <span aria-hidden tw="flex-shrink-0 pr-1">
          {prefix}
        </span>
      )}
      <span>{children}</span>
      {postfix && (
        <span aria-hidden tw="flex-shrink-0 ml-auto pl-1">
          {postfix}
        </span>
      )}
    </>
  );

  if ('href' in polymorphicProps || 'to' in polymorphicProps) {
    const { innerRef, ...forwardedProps } = polymorphicProps;

    return (
      <AbstractDropdownItemInteractive
        {...forwardedProps}
        ref={innerRef}
        as={'to' in polymorphicProps ? Link : 'a'}
        id={itemID}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        tabIndex={-1}
      >
        {content}
      </AbstractDropdownItemInteractive>
    );
  }

  const { innerRef, ...forwardedProps } = polymorphicProps;

  return (
    <AbstractDropdownItemInteractive
      {...forwardedProps}
      ref={innerRef}
      as="button"
      id={itemID}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      tabIndex={-1}
      type="button"
    >
      {content}
    </AbstractDropdownItemInteractive>
  );
}

const AbstractDropdownItemInteractive = styled(AbstractDropdownItemStatic)<{ disabled?: boolean }>(({ disabled }) => [
  tw`
    cursor-pointer
    flex
    focus:(bg-gray-5 outline-none text-primary)
    items-center
  `,
  disabled && tw`pointer-events-none text-gray-50`,
]);
